import React, { useEffect } from "react";
import {
  Paper,
  Typography,
  Skeleton,
  Stack,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { format } from "date-fns";
import { LoyaltyTransaction, LoyaltyTransactionType } from "@/types/loyalty";
import { useAppDispatch, useAppSelector } from "@/hooks";
import { fetchUsers } from "@/store/usersSlice";

interface Props {
  transactions: LoyaltyTransaction[];
  isLoading: boolean;
  isSuccess: boolean;
}

const LoyaltyTransactionsList: React.FC<Props> = ({
  transactions,
  isLoading,
  isSuccess,
}) => {
  const dispatch = useAppDispatch();
  const users = useAppSelector((state) => state.users);

  // Fetch missing users
  useEffect(() => {
    if (isSuccess && transactions.length > 0) {
      const uidsToFetch = transactions
        .map((t) => t.createdBy)
        .filter((uid): uid is string => !!uid && !users[uid]);

      const uniqueUids = Array.from(new Set(uidsToFetch));

      if (uniqueUids.length > 0) {
        dispatch(fetchUsers(uniqueUids));
      }
    }
  }, [transactions, isSuccess, users, dispatch]);

  const columns: GridColDef<LoyaltyTransaction>[] = [

    {
      field: "createdBy",
      headerName: "Procesado por",
      flex: 1,
      renderCell: (params) => {
        const uid = params.value as string;
        return uid ? (users[uid]?.name || "...") : "Sistema";
      },
    },
    {
      field: "reference",
      headerName: "Referencia",
      flex: 1,
      align: "left",
      headerAlign: "left",
      renderCell: (params) => {
        const reference = params.row.reference || "-";
        const displayValue = reference.length > 50 ? `${reference.substring(0, 50)}...` : reference;
        return (
          <Typography variant="body1">
            {displayValue}
          </Typography>
        );
      },
    },
    {
      field: "points",
      headerName: "Balance",
      flex: 1,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => {
        const tx = params.row;
        const isPositive = tx.type === LoyaltyTransactionType.EARN || (tx.type === LoyaltyTransactionType.MANUAL && tx.points > 0);
        const sign = isPositive ? "+" : "-";
        const color = isPositive ? "success.main" : "error.main";

        return (
          <Typography sx={{ color, fontWeight: "400" }}>
            {sign}{Math.abs(tx.points)}
          </Typography>
        );
      },
    },

    {
      field: "createdAt",
      headerName: "Fecha",
      flex: 1,
      align: "right",
      headerAlign: "right",
      valueFormatter: (params) => format(params.value as number, "dd/MM/yy"),
    },

  ];

  if (isLoading) {
    return (
      <Stack spacing={2} sx={{ mt: 4 }}>
        <Typography variant="h3" gutterBottom>
          Historial de transacciones
        </Typography>
        <Skeleton variant="rectangular" height={300} sx={{ borderRadius: 2 }} />
      </Stack>
    );
  }

  if (!isSuccess || transactions.length === 0) {
    return null;
  }

  return (
    <Stack spacing={2} sx={{ mt: 4 }}>
      <Typography variant="h3" gutterBottom>
        Historial de transacciones
      </Typography>
      <Paper sx={{ height: 400, width: "100%", borderRadius: 2, overflow: "hidden" }}>
        <DataGrid
          rows={transactions}
          columns={columns}
          disableRowSelectionOnClick
          density="standard"
        />
      </Paper>
    </Stack>
  );
};

export default LoyaltyTransactionsList;
